import { BottomTabBarProps, createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { useCallback, useMemo } from 'react';

import { SvgIcon } from '@/components/ui';
import {
  blurTargetRefHome,
  blurTargetRefPlayer,
  blurTargetRefPlayerPathway,
  Tabbar,
} from '@/components/ui/tabbar';
import { STRINGS } from '@/constants/strings';
import { CoachNotificationsTabIcon } from '@/features/coach/components/notifications';
import { CoachNotificationsScreen } from '@/features/coach/screens/coach-notifications-screen';
import { PlayerPathwayScreen } from '@/features/player-pathway/screens/player-pathway-screen';
import { useScaling } from '@/hooks';
import { HomeStackNavigator } from '@/navigations/auth-stack/home-stack';
import { PlayerStackNavigator } from '@/navigations/auth-stack/player-stack';
import { HomeTabNavigatorParamList } from '@/navigations/types';
import { colors } from '@/theme/colors';

const Tab = createBottomTabNavigator<HomeTabNavigatorParamList>();

export function HomeTabNavigator() {
  const { scale } = useScaling();
  const renderTabBar = useCallback((props: BottomTabBarProps) => {
    const activeRoute = props.state.routes[props.state.index];
    const blurTargetRef =
      activeRoute.name === 'Home'
        ? blurTargetRefHome
        : activeRoute.name === 'Player'
          ? blurTargetRefPlayer
          : activeRoute.name === 'PlayerPathway'
            ? blurTargetRefPlayerPathway
            : undefined;
    return <Tabbar {...props} blurTargetRef={blurTargetRef} />;
  }, []);
  const renderHomeIcon = useCallback(
    ({ focused }: { focused: boolean }) => (
      <SvgIcon name="IcHome" size={scale(24)} color={focused ? 'white' : colors.Blue} />
    ),
    [scale],
  );
  const renderPlayerIcon = useCallback(
    ({ focused }: { focused: boolean }) => (
      <SvgIcon name="IcPlayer" size={scale(24)} color={focused ? 'white' : colors.Blue} />
    ),
    [scale],
  );
  const renderPlayerPathwayIcon = useCallback(
    ({ focused }: { focused: boolean }) => (
      <SvgIcon name="IcPlayerPathway" size={scale(24)} color={focused ? 'white' : colors.Blue} />
    ),
    [scale],
  );
  const renderNotificationsIcon = useCallback(
    ({ focused }: { focused: boolean }) => <CoachNotificationsTabIcon focused={focused} />,
    [],
  );

  // Memoize per-screen options so the descriptor content received by `Tabbar` stays referentially
  // stable across renders. Combined with the stabilized callbacks in `Tabbar`, this lets the
  // memoized `TabbarItem` skip re-renders when nothing relevant has changed.
  const homeOptions = useMemo(
    () => ({
      title: STRINGS.appDocumentTitle,
      tabBarLabel: STRINGS.common.navigation.tabDashboard,
      tabBarIcon: renderHomeIcon,
    }),
    [renderHomeIcon],
  );
  const playerOptions = useMemo(
    () => ({
      title: STRINGS.appDocumentTitle,
      tabBarLabel: STRINGS.common.navigation.tabPlayers,
      tabBarIcon: renderPlayerIcon,
    }),
    [renderPlayerIcon],
  );
  const playerPathwayOptions = useMemo(
    () => ({
      title: STRINGS.appDocumentTitle,
      tabBarLabel: STRINGS.common.navigation.tabPlayerPathway,
      tabBarIcon: renderPlayerPathwayIcon,
    }),
    [renderPlayerPathwayIcon],
  );
  const notificationsOptions = useMemo(
    () => ({
      title: STRINGS.appDocumentTitle,
      tabBarLabel: STRINGS.common.navigation.tabNotifications,
      tabBarIcon: renderNotificationsIcon,
    }),
    [renderNotificationsIcon],
  );

  return (
    <Tab.Navigator
      tabBar={renderTabBar}
      backBehavior="none"
      screenOptions={{
        lazy: true,
        freezeOnBlur: true,
        headerShown: false,
        title: STRINGS.appDocumentTitle,
      }}>
      <Tab.Screen name="Home" options={homeOptions} component={HomeStackNavigator} />
      <Tab.Screen name="Player" options={playerOptions} component={PlayerStackNavigator} />
      <Tab.Screen
        name="PlayerPathway"
        options={playerPathwayOptions}
        component={PlayerPathwayScreen}
      />
      <Tab.Screen
        name="Notifications"
        options={notificationsOptions}
        component={CoachNotificationsScreen}
      />
    </Tab.Navigator>
  );
}
